import { Navigate, Outlet } from "react-router";
import { useAccessFeatures } from "@/auth/access-features";

// Pathless layout-route guard for a Genesis 242-access-model feature key
// (grow, match, search, …). A React Router loader can't call this - loaders
// run outside the component tree, and the feature set lives in
// AccessFeaturesProvider's context, not a store a loader could read
// synchronously - so this is a route element wrapping the feature's children
// in <Outlet/>, same shape as AppShell wrapping the whole app.
//
// Redirects to the app root rather than a "forbidden" page: a direct hit on
// a gated route is not an error the visitor needs to see, it's a link that
// doesn't apply to them (bookmark, stale nav, deep link from an email).
//
// Renders nothing until the whitelist's first answer arrives, rather than
// redirecting: this component renders on first paint, before the network
// round trip resolves, and hasFeature() answers false during that window by
// design (see access-features.tsx) - firing Navigate() on that transient
// false would bounce an allowed visitor off the page on every reload, before
// the real answer ever arrives.
export function RequireFeature({ featureKey }: { featureKey: string }) {
  const { hasFeature, pending } = useAccessFeatures();
  if (pending) return null;
  if (!hasFeature(featureKey)) return <Navigate to="/" replace />;
  return <Outlet />;
}
